import { formatMoney, t } from "@hakili/i18n";
import {
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_TEXT_2,
  Callout,
  Chip,
  GuardedButton,
  Input,
  Textarea,
  Toast,
  ToastStack,
} from "@hakili/ui";
import { ChevronRight, Info, Lock } from "lucide-react";
import { type ReactElement, useState } from "react";

import { DASH, fill } from "./dossiers.js";

/**
 * `COMITE-RECORD-2` (D-1024-2, D-1024-7, ratified D-1025-4; frames 110 to 113, D2-copy « Chef, file » and « Chef, écran »):
 * **the chef records the committee's outcome himself**, on `/comite/:reference`, from « Comités à enregistrer » on Décisions.
 *
 * The screen draws what the door sends and sends what the form holds. WHY a file sits before the committee is the SERVER's answer
 * (`committeeReasonsOf`, kept on the file at the buyer's acceptance): the chips and the callout are made from those reasons and
 * never from a comparison made here. The recorder and the day are stamped by the server, so the form asks for neither; the date
 * is the SITTING's, typed jj/mm/aaaa (the kit has no date field yet) and sent as the day it names. A refusal of the door is shown in
 * its own words. What is not built is not drawn: no correction of a recorded decision, no calendar, no documents.
 */

export type CommitteeOutcome = "accorde" | "accordeSousConditions" | "refuse" | "ajourne";

/** A reason the file sits before the committee, as the server kept it (`CommitteeReason` of `domain-types`). */
export type CommitteeReasonView =
  | { readonly kind: "amount"; readonly amountFinanced: number; readonly thresholdAmount: number }
  | { readonly kind: "rule"; readonly code: string; readonly rule: string; readonly detail: string };

export interface CommitteeSittingView {
  readonly sequence: number;
  readonly sittingDate: string;
  readonly outcome: CommitteeOutcome;
  readonly summary: string;
  readonly conditions: readonly string[];
  readonly recordedBy: string;
  readonly recordedOn: string;
}

/** What the decision read says of the committee: the reasons kept and the sittings, oldest first. */
export interface CommitteeRead {
  readonly reasons: readonly CommitteeReasonView[];
  readonly sittings: readonly CommitteeSittingView[];
}

/** A file of the chef's « Comités à enregistrer », as the queue's committee card sends it. */
export interface CommitteeFileView {
  readonly bankFileId: string;
  readonly reference: string;
  readonly clientName: string | null;
  readonly amountFinanced: number;
  readonly waitingDays: number;
  readonly reasons: readonly CommitteeReasonView[];
  readonly adjournedOn: string | null;
  readonly adjournments: number;
  /**
   * `COMITE-BEFORE-OFFER` (`CHEF-SELF-VALIDATION` R2, frame 193): the SERVED boolean, true when the chef sent her own file to the comité before any offer
   * left. Read as sent: only `true` is true, and the screen infers nothing (no stage, no amount) to say it.
   */
  readonly beforeOffer: boolean;
}

/** The door's body (`POST /bank/decisions/{id}/committee`): the sitting's day, the issue, the conditions, the summary. */
export interface CommitteeBody {
  readonly sittingDate: string;
  readonly outcome: CommitteeOutcome;
  readonly conditions: readonly string[];
  readonly summary: string;
}

const OUTCOMES: readonly CommitteeOutcome[] = ["accorde", "accordeSousConditions", "refuse", "ajourne"];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" ? value : null);

function reasonFrom(raw: unknown): CommitteeReasonView | null {
  const row = asRecord(raw);
  if (row === null) return null;
  if (row["kind"] === "amount") {
    const amountFinanced = num(row["amountFinanced"]);
    const thresholdAmount = num(row["thresholdAmount"]);
    return amountFinanced === null || thresholdAmount === null
      ? null
      : { kind: "amount", amountFinanced, thresholdAmount };
  }
  if (row["kind"] === "rule") {
    const rule = str(row["rule"]);
    const detail = str(row["detail"]);
    return rule === null || detail === null
      ? null
      : { kind: "rule", code: str(row["code"]) ?? "", rule, detail };
  }
  return null;
}

const reasonsFrom = (raw: unknown): readonly CommitteeReasonView[] =>
  Array.isArray(raw) ? raw.map(reasonFrom).filter((one): one is CommitteeReasonView => one !== null) : [];

/** The queue's committee card: its files (the counsel and the sessions are not read, not built). */
export function committeeFilesFrom(raw: unknown): readonly CommitteeFileView[] {
  const files = asRecord(raw)?.["files"];
  if (!Array.isArray(files)) return [];
  return files.flatMap((one): CommitteeFileView[] => {
    const row = asRecord(one);
    const bankFileId = str(row?.["bankFileId"]);
    if (row === null || bankFileId === null) return [];
    return [
      {
        bankFileId,
        reference: str(row["reference"]) ?? "",
        clientName: str(row["clientName"]),
        amountFinanced: num(row["amountFinanced"]) ?? 0,
        waitingDays: num(row["waitingDays"]) ?? 0,
        reasons: reasonsFrom(row["reasons"]),
        adjournedOn: str(row["adjournedOn"]),
        adjournments: num(row["adjournments"]) ?? 0,
        beforeOffer: row["beforeOffer"] === true,
      },
    ];
  });
}

/** The decision read's committee: `null` for a file that was never at the comité. */
export function committeeReadFrom(raw: unknown): CommitteeRead | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const sittings = Array.isArray(row["sittings"])
    ? row["sittings"].flatMap((one): CommitteeSittingView[] => {
        const sitting = asRecord(one);
        const outcome = OUTCOMES.find((value) => value === sitting?.["outcome"]);
        const sequence = num(sitting?.["sequence"]);
        const sittingDate = str(sitting?.["sittingDate"]);
        const summary = str(sitting?.["summary"]);
        if (
          sitting === null ||
          outcome === undefined ||
          sequence === null ||
          sittingDate === null ||
          summary === null
        )
          return [];
        return [
          {
            sequence,
            sittingDate,
            outcome,
            summary,
            conditions: Array.isArray(sitting["conditions"])
              ? sitting["conditions"].filter((line): line is string => typeof line === "string")
              : [],
            recordedBy: str(sitting["recordedBy"]) ?? "",
            recordedOn: str(sitting["recordedOn"]) ?? "",
          },
        ];
      })
    : [];
  return { reasons: reasonsFrom(row["reasons"]), sittings };
}

/** The chip of « Pourquoi le comité » (frame 110). */
export const reasonChipWords = (reason: CommitteeReasonView): string =>
  reason.kind === "amount"
    ? t("bank.comite.raisonMontant")
    : fill("bank.comite.raisonRegle", { regle: reason.rule });

/** The sentence of the callout « Pourquoi ce dossier passe en comité » (D2-copy). */
export const reasonSentence = (reason: CommitteeReasonView): string => {
  if (reason.kind === "amount") {
    return fill("bank.comite.phraseMontant", {
      montant: formatMoney(reason.amountFinanced),
      seuil: formatMoney(reason.thresholdAmount),
    });
  }
  const detail = reason.detail.trim().replace(/\.$/u, "");
  return fill("bank.comite.phraseRegle", {
    regle: reason.rule,
    detail: detail.charAt(0).toLowerCase() + detail.slice(1),
  });
};

/** « jj/mm/aaaa » as the day it names (`YYYY-MM-DD`), or `null` for a text that is no real day. */
export function isoOfDay(typed: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u.exec(typed.trim());
  if (match === null) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const at = new Date(Date.UTC(year, month - 1, day));
  if (at.getUTCFullYear() !== year || at.getUTCMonth() !== month - 1 || at.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** `YYYY-MM-DD` as « jj/mm/aaaa »; what does not parse stays as the door sent it (R81, never a throw). */
export function dayLabel(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/u.exec(iso);
  return match === null ? iso : `${match[3] ?? ""}/${match[2] ?? ""}/${match[1] ?? ""}`;
}

const OUTCOME_WORDS: Readonly<Record<CommitteeOutcome, readonly [string, string]>> = {
  accorde: ["bank.comite.accorde", "bank.comite.accordeAide"],
  accordeSousConditions: ["bank.comite.accordeSousConditions", "bank.comite.accordeSousConditionsAide"],
  refuse: ["bank.comite.refuse", "bank.comite.refuseAide"],
  ajourne: ["bank.comite.ajourne", "bank.comite.ajourneAide"],
};

/** The sentence the drawing's toast says for each outcome, drawn above the form once the door answered. */
export const noticeOf = (outcome: CommitteeOutcome): string =>
  outcome === "refuse"
    ? t("bank.comite.noticeRefus")
    : outcome === "ajourne"
      ? t("bank.comite.noticeAjourne")
      : t("bank.comite.noticeAccord");

const NBSP = "\u00a0";
const rateWords = (ratePct: string): string => `${ratePct.replace(".", ",")}${NBSP}%`;

/** D2's ⓘ on an information bar (`callout("info", …)`): 111's bar and the analyst's 43 and 44 draw the same one, from here. */
export const COMMITTEE_INFO_ICON = <Info className="h-5 w-5 text-indigo-600" aria-hidden="true" />;

/** The file's facts, as the decision read carries them. */
export interface RecordingFile {
  readonly reference: string;
  readonly clientName: string | null;
  readonly amountFinanced: number;
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly delegationThresholdAmount: number | null;
  readonly stage: string;
  readonly committee: CommitteeRead | null;
}

/** A sentence-bearing chip is 14 px, wrapping if it must (the theme's floor: a chip is 13 px only as a label). */
const SENTENCE_CHIP = "!text-[length:var(--hk-fs-body)] !leading-snug whitespace-normal text-left";

/** « Comités à enregistrer » (frame 110): one row per file at the comité, the reasons the server kept, the sitting's state. */
export function CommitteesToRecord(props: {
  readonly files: readonly CommitteeFileView[];
  readonly onOpen: (reference: string) => void;
}): ReactElement | null {
  if (props.files.length === 0) return null;
  return (
    <div
      className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white"
      data-testid="bank-decisions-comites"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-5 pb-3 pt-4">
        <h2 className="text-base font-semibold" data-testid="bank-decisions-comites-titre">
          {t("bank.decisions.comitesTitre")}
        </h2>
        <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-decisions-comites-sous">
          {t("bank.decisions.comitesSous")}
        </p>
      </div>
      <table className="w-full text-left text-sm">
        <thead>
          <tr
            className={`border-y border-gray-200 bg-gray-50 ${BANK_FS_META} uppercase tracking-wide text-gray-500`}
          >
            <th scope="col" className="px-5 py-2 font-medium">
              {t("bank.decisions.comitesColDossier")}
            </th>
            <th scope="col" className="py-2 pr-3 font-medium">
              {t("bank.decisions.comitesColMontant")}
            </th>
            <th scope="col" className="py-2 pr-3 font-medium">
              {t("bank.decisions.comitesColPourquoi")}
            </th>
            <th scope="col" className="py-2 pr-5 font-medium">
              {t("bank.decisions.comitesColSeance")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {props.files.map((file) => (
            <tr key={file.bankFileId} className="align-middle hover:bg-gray-50">
              <td className="px-5 py-3">
                <button
                  type="button"
                  onClick={() => {
                    props.onOpen(file.reference);
                  }}
                  className="cursor-pointer text-left"
                  data-testid={`bank-comite-ligne-${file.bankFileId}`}
                >
                  <span className="block text-sm font-medium text-gray-900">{file.clientName ?? DASH}</span>
                  <span className={`mt-0.5 block ${BANK_FS_META} text-gray-500`} data-meta="">
                    {file.reference}
                  </span>
                  {file.beforeOffer ? (
                    // `COMITE-BEFORE-OFFER` (frame 193): the comité rules BEFORE the offer; the served boolean, nothing computed here.
                    <span
                      className={`mt-0.5 block ${BANK_FS_META} text-gray-500`}
                      data-meta=""
                      data-testid={`bank-comite-avant-offre-${file.bankFileId}`}
                    >
                      {t("bank.comite.avantOffre")}
                    </span>
                  ) : null}
                </button>
              </td>
              <td className="py-3 pr-3 font-mono text-sm tabular-nums text-gray-900">
                {formatMoney(file.amountFinanced)}
              </td>
              <td className="py-3 pr-3">
                <span className="flex flex-wrap gap-2" data-testid={`bank-comite-raisons-${file.bankFileId}`}>
                  {file.reasons.map((reason, at) =>
                    reason.kind === "amount" ? (
                      // A label (no verb, no stop): the theme's 13 px, one line.
                      <span key={`${reason.kind}-${String(at)}`} data-kind="chip-label">
                        <Chip tone="neutre">{reasonChipWords(reason)}</Chip>
                      </span>
                    ) : (
                      <Chip key={`${reason.kind}-${String(at)}`} tone="attention" className={SENTENCE_CHIP}>
                        {reasonChipWords(reason)}
                      </Chip>
                    ),
                  )}
                </span>
              </td>
              <td className="py-3 pr-5">
                <button
                  type="button"
                  onClick={() => {
                    props.onOpen(file.reference);
                  }}
                  className="cursor-pointer"
                  data-testid={`bank-comite-seance-${file.bankFileId}`}
                >
                  {file.adjournedOn === null ? (
                    <Chip tone="neutre" className={SENTENCE_CHIP}>
                      {t("bank.comite.passe")}
                    </Chip>
                  ) : (
                    <span data-kind="chip-label">
                      <Chip tone="neutre">
                        {fill("bank.comite.ajourneLe", { date: dayLabel(file.adjournedOn) })}
                      </Chip>
                    </span>
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Row = (props: {
  readonly label: string;
  readonly children: ReactElement | string;
  /** D2's `mono` cell: a figure drawn in the monospaced face. */
  readonly mono?: boolean;
}): ReactElement => (
  <div className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2.5 last:border-b-0">
    <dt className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>{props.label}</dt>
    <dd
      className={`m-0 text-right text-sm font-medium tabular-nums text-gray-900${props.mono === true ? " font-mono" : ""}`}
    >
      {props.children}
    </dd>
  </div>
);

/** « Passage en comité · {client} »: the form (111, 112), the adjournment's warning (113), or the recorded decision. */
export function Committee(props: {
  readonly file: RecordingFile;
  readonly busy: boolean;
  readonly refusal: string | null;
  /** The drawing's sentence for the outcome just recorded, or `null`. */
  readonly notice: string | null;
  readonly onBack: () => void;
  readonly onRecord: (body: CommitteeBody) => void;
}): ReactElement {
  const { file } = props;
  const [day, setDay] = useState("");
  const [outcome, setOutcome] = useState<CommitteeOutcome | null>(null);
  const [conditions, setConditions] = useState("");
  const [summary, setSummary] = useState("");
  const sittings = file.committee?.sittings ?? [];
  const reasons = file.committee?.reasons ?? [];
  const lastAdjourned = [...sittings].reverse().find((one) => one.outcome === "ajourne") ?? null;
  const recorded = file.stage !== "comite";
  const lines = conditions
    .split("\n")
    .map((one) => one.trim())
    .filter((one) => one !== "");
  const isoDay = isoOfDay(day);
  const missing = [
    ...(isoDay === null ? [t("bank.comite.gardeDate")] : []),
    ...(outcome === null ? [t("bank.comite.gardeIssue")] : []),
    ...(outcome === "accordeSousConditions" && lines.length === 0 ? [t("bank.comite.gardeConditions")] : []),
    ...(summary.trim() === "" ? [t("bank.comite.gardeResume")] : []),
  ];
  const reason =
    missing.length > 0
      ? fill("bank.comite.garde", { liste: missing.join(", ") })
      : props.busy
        ? t("bank.comite.enCours")
        : null;
  const last = sittings[sittings.length - 1] ?? null;

  const facts = (
    <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="bank-comite-dossier">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="m-0 text-base font-semibold">{t("bank.comite.dossier")}</h2>
        <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comite.dossierSous")}</p>
      </div>
      <dl className="m-0">
        <Row label={t("bank.comite.client")}>{file.clientName ?? DASH}</Row>
        <Row label={t("bank.comite.montant")} mono>
          {formatMoney(file.amountFinanced)}
        </Row>
        <Row label={t("bank.comite.duree")}>
          {fill("bank.comite.dureeMois", { n: String(file.durationMonths) })}
        </Row>
        <Row label={t("bank.comite.taux")} mono>
          {rateWords(file.nominalRatePct)}
        </Row>
        {file.delegationThresholdAmount === null ? null : (
          <Row label={t("bank.comite.seuil")} mono>
            {formatMoney(file.delegationThresholdAmount)}
          </Row>
        )}
      </dl>
    </div>
  );

  return (
    <div data-testid="bank-comite">
      {/* D2's crumb: « File de décision › Passage en comité », one line of metadata (13 px). */}
      <div className={`mb-3 flex items-center gap-1.5 ${BANK_FS_META} text-gray-600`}>
        <button
          type="button"
          onClick={props.onBack}
          className="hover:text-indigo-600"
          data-testid="bank-comite-retour"
        >
          {t("bank.decision.fileRetour")}
        </button>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="font-medium text-gray-900" data-testid="bank-comite-fil-courant">
          {t("bank.comite.filCourant")}
        </span>
      </div>
      <div className="mb-5">
        <h1 className="m-0 text-2xl font-semibold tracking-tight" data-testid="bank-comite-titre">
          {fill("bank.comite.titre", { client: file.clientName ?? DASH })}
        </h1>
        <p className={`mt-1.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`} data-testid="bank-comite-sous-titre">
          {fill("bank.comite.sousTitre", {
            reference: file.reference,
            montant: formatMoney(file.amountFinanced),
            duree: String(file.durationMonths),
          })}
        </p>
      </div>

      {props.notice === null ? null : (
        <ToastStack>
          <div data-testid="bank-comite-notice">
            <Toast>{props.notice}</Toast>
          </div>
        </ToastStack>
      )}

      {recorded ? (
        <>
          <Callout tone="ok" testId="bank-comite-enregistre" className="mb-4">
            <strong>{t("bank.comite.enregistre")}</strong> {t("bank.comite.enregistreSuite")}
          </Callout>
          {last === null ? null : (
            <div
              className="rounded-2xl border border-gray-200 bg-white p-5"
              data-testid="bank-comite-decision"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="m-0 text-base font-semibold">{t("bank.comite.decision")}</h2>
                <Chip tone={last.outcome === "refuse" ? "alerte" : "ok"}>
                  {t(OUTCOME_WORDS[last.outcome][0])}
                </Chip>
              </div>
              <dl className="m-0">
                <Row label={t("bank.comite.decisionSeance")}>{dayLabel(last.sittingDate)}</Row>
                {last.conditions.length === 0 ? null : (
                  <Row label={t("bank.comite.decisionConditions")}>
                    <span className="block text-left">
                      {last.conditions.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </span>
                  </Row>
                )}
                <Row label={t("bank.comite.decisionResume")}>
                  <span className="block text-left font-normal">{last.summary}</span>
                </Row>
              </dl>
              <p className={`mt-3 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                {fill("bank.comite.decisionPar", { nom: last.recordedBy, date: dayLabel(last.recordedOn) })}
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          {lastAdjourned === null ? null : (
            <Callout tone="warn" testId="bank-comite-ajourne" className="mb-3">
              <strong>{t("bank.comite.dejaAjourne")}</strong>{" "}
              {fill("bank.comite.seanceDu", {
                date: dayLabel(lastAdjourned.sittingDate),
                resume: lastAdjourned.summary,
              })}
            </Callout>
          )}
          {reasons.length === 0 ? null : (
            <Callout tone="info" testId="bank-comite-pourquoi" className="mb-4" icon={COMMITTEE_INFO_ICON}>
              <strong>{t("bank.comite.pourquoi")}</strong> {reasons.map(reasonSentence).join(" ")}{" "}
              {t("bank.comite.pourquoiFin")}
            </Callout>
          )}
          {props.refusal === null ? null : (
            <Callout tone="bad" testId="bank-comite-refus" className="mb-3">
              {props.refusal}
            </Callout>
          )}
          <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-start gap-4">
            <form
              className="rounded-2xl border border-gray-200 bg-white p-5"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
              }}
              aria-label={t("bank.comite.carte")}
            >
              <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="m-0 text-base font-semibold">{t("bank.comite.carte")}</h2>
                <p className={`m-0 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comite.carteSous")}</p>
              </div>
              <div className="mb-4">
                <label htmlFor="bank-comite-date" className="mb-1.5 block text-sm font-medium text-gray-900">
                  {t("bank.comite.date")}
                </label>
                <Input
                  id="bank-comite-date"
                  testId="bank-comite-date"
                  inputMode="numeric"
                  value={day}
                  placeholder={t("bank.comite.datePlaceholder")}
                  readOnly={props.busy}
                  className="!font-normal"
                  onChange={setDay}
                />
              </div>
              <fieldset className="m-0 mb-4 border-0 p-0">
                <legend className="mb-1.5 p-0 text-sm font-medium text-gray-900">
                  {t("bank.comite.issue")}
                </legend>
                <div className="grid gap-2">
                  {OUTCOMES.map((value) => (
                    <label
                      key={value}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 ${
                        outcome === value ? "border-indigo-600 bg-indigo-50" : "border-gray-300 bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="bank-comite-issue"
                        value={value}
                        checked={outcome === value}
                        disabled={props.busy}
                        onChange={() => {
                          setOutcome(value);
                        }}
                        className="mt-1"
                        data-testid={`bank-comite-issue-${value}`}
                      />
                      <span data-testid={`bank-comite-issue-${value}-libelle`}>
                        <span className="block text-sm font-semibold text-gray-900">
                          {t(OUTCOME_WORDS[value][0])}
                        </span>
                        <span className={`block ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
                          {t(OUTCOME_WORDS[value][1])}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              {outcome === "accordeSousConditions" ? (
                <div className="mb-4">
                  <label
                    htmlFor="bank-comite-conditions"
                    className="mb-1.5 block text-sm font-medium text-gray-900"
                  >
                    {t("bank.comite.conditions")}
                  </label>
                  <Textarea
                    id="bank-comite-conditions"
                    testId="bank-comite-conditions"
                    rows={3}
                    value={conditions}
                    readOnly={props.busy}
                    className="!font-normal"
                    onChange={setConditions}
                  />
                  <p className={`mt-1.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comite.conditionsAide")}</p>
                </div>
              ) : null}
              <div className="mb-5">
                <label
                  htmlFor="bank-comite-resume"
                  className="mb-1.5 block text-sm font-medium text-gray-900"
                >
                  {t("bank.comite.resume")}
                </label>
                <Textarea
                  id="bank-comite-resume"
                  testId="bank-comite-resume"
                  rows={5}
                  value={summary}
                  readOnly={props.busy}
                  className="!font-normal"
                  onChange={setSummary}
                />
                <p className={`mt-1.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.comite.resumeAide")}</p>
              </div>
              {reason !== null ? (
                <GuardedButton
                  variant="primary"
                  testId="bank-comite-enregistrer"
                  disabled
                  reason={reason}
                  reasonDress="chip"
                  reasonIcon={<Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                >
                  {t("bank.comite.enregistrer")}
                </GuardedButton>
              ) : (
                <GuardedButton
                  variant="primary"
                  testId="bank-comite-enregistrer"
                  onClick={() => {
                    if (isoDay === null || outcome === null) return;
                    props.onRecord({
                      sittingDate: isoDay,
                      outcome,
                      conditions: outcome === "accordeSousConditions" ? lines : [],
                      summary: summary.trim(),
                    });
                  }}
                >
                  {t("bank.comite.enregistrer")}
                </GuardedButton>
              )}
            </form>
            {facts}
          </div>
        </>
      )}
    </div>
  );
}
